import React, { useEffect, useMemo } from "react";
import {
  IContactProduct,
  ProductDto
} from "api/GeneratedClients/ContactsClient";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import ClearIcon from "@mui/icons-material/Clear";
import Chip from "@mui/material/Chip";
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput
} from "@mui/material";
import { strings } from "localization";
import { sortBy } from "lodash-es";

interface ScopeOfWorkProps {
  vendorProducts: ProductDto[];
  contactProducts: IContactProduct[];
  setContactProducts: React.Dispatch<React.SetStateAction<IContactProduct[]>>;
}

export const ContactScopeOfWork = ({
  vendorProducts,
  contactProducts,
  setContactProducts
}: ScopeOfWorkProps) => {
  const vendorProductsProduct = useMemo(
    () =>
      vendorProducts.map(vp => ({
        ...vp.product,
        productTypeId: vp.product?.id,
        vendorProductId: vp.id
      })),
    [vendorProducts]
  );

  const [options, setOptions] = React.useState<(string | undefined)[]>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<
    (string | undefined)[]
  >([]);

  useEffect(() => {
    const contactProductCodes = contactProducts.map(p => p.code);
    const vendorProductCodes = vendorProductsProduct
      ?.map(vp => vp?.code)
      .filter(vp => !contactProductCodes.includes(vp as string));
    const sortedSelectedOptions = sortBy(contactProductCodes);
    const sortedOptions = sortBy(vendorProductCodes);
    setSelectedOptions(sortedSelectedOptions);
    setOptions(sortedOptions);
  }, [contactProducts, vendorProducts, vendorProductsProduct]);

  const handleChange = (event: SelectChangeEvent<typeof selectedOptions>) => {
    const {
      target: { value: codes }
    } = event;
    setSelectedOptions(codes as (string | undefined)[]);
    const contactProducts = vendorProductsProduct
      .filter(vp => codes.includes(vp.code as string))
      .map(vp => ({
        vendorProductId: vp.vendorProductId,
        code: vp.code,
        productTypeId: vp.productTypeId
      }));
    setContactProducts(contactProducts as IContactProduct[]);
  };

  const handDelete = (code: string) => {
    const newSelectedCode = selectedOptions.filter(
      selectedCode => selectedCode !== code
    );
    const newOptions = [...options];
    newOptions.push(code);
    setOptions(newOptions);
    setSelectedOptions(newSelectedCode);
    const newContactProducts = contactProducts?.filter(cp => cp.code !== code);
    setContactProducts(newContactProducts);
  };
  return (
    <FormControl fullWidth style={{ marginTop: "2.4rem" }}>
      <InputLabel id="scopes-multiselect-label">
        {strings.contactManagement.contactListModal.scopesLabel}
      </InputLabel>
      <Select
        labelId="scopes-multiselect"
        id="scopes-multiselect-select"
        multiple
        value={selectedOptions}
        onChange={handleChange}
        input={<OutlinedInput label="Scopes" />}
        renderValue={selected => {
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5
              }}
            >
              {selected.map(code => (
                <Chip
                  key={code}
                  label={code}
                  clickable
                  onMouseDown={event => event.stopPropagation()}
                  onDelete={() => handDelete(code as string)}
                />
              ))}
            </Box>
          );
        }}
        endAdornment={
          selectedOptions?.length > 0 && (
            <InputAdornment
              style={{
                position: "absolute",
                right: "3rem"
              }}
              position="end"
            >
              <IconButton
                style={{ padding: 0 }}
                onClick={() => setContactProducts([])}
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          )
        }
      >
        {options?.length === 0 && (
          <FormHelperText style={{ padding: "0 1rem", textAlign: "center" }}>
            {strings.contactManagement.contactListModal.emptyScopeOfWorksText}
          </FormHelperText>
        )}
        {options?.length !== 0 &&
          options?.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
